import {Observable} from 'rxjs';

import {switchMap, tap} from 'rxjs/operators';
import {Injectable, Injector} from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import {AuthService} from './auth.service';
import {LoginService} from '../login/login.service';
import {Location} from '@angular/common';
import {environment} from '../../../environments/environment';
import {Utils} from '../../shared/utils/utils';
import {SnackBarService} from '../services/snackbar.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private excludeHeaderUrls = ['moments.videobyte.com', 'moments.dev.dh.videobyte.tech', 'vb-prod-logger-db-results', environment.auth0.domain];

  constructor(private injector: Injector,
              private location: Location) {
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // do not instance authService/loginService in construction due to circular reference error
    const authService = this.injector.get(AuthService);
    const loginService = this.injector.get(LoginService);
    const snackBarService = this.injector.get(SnackBarService);

    const handle = (req: HttpRequest<any>) => next.handle(req).pipe(tap(event => {
        // all good
      }, (err: any) => {
        if (err instanceof HttpErrorResponse) {
          if (err.status === 401) {
            // Drop cached token so a retry (or next page) does not keep sending a dead JWT.
            authService.clearCachedToken();
            // Session is actually invalid/expired - only this warrants forcing a full logout.
            const path = this.location.path(false);
            if (path !== '/login' && path !== '/' && path !== '') {
              loginService.logoutWithReturnUrl(path);
            } else {
              loginService.logout();
            }
          } else if (err.status === 403) {
            // Authenticated but not permitted for this specific action - not a session problem,
            // so don't force the user all the way back out to Auth0 for it.
            snackBarService.error('You don\'t have permission to do that.');
          }
        }
      }
    ));

    //Some urls do not need header, this way we skip attaching one for them
    if (this.excludeHeaderUrls.some(excluded => request.url?.includes(excluded))) {
      return handle(request);
    }

    return authService.getAccessToken().pipe(
      switchMap(token => {
        // No token means either the user isn't authenticated yet (e.g. requests fired from the
        // login page itself, like the public assets/version.json check) or the Auth0 session
        // genuinely expired. We don't force a logout redirect here - a real unauthorized backend
        // response is already handled below via the 401/403 branch.
        if (token) {
          request = request.clone({
            setHeaders: {
              Authorization: `Bearer ${token}`,
              'X-Device-Name': Utils.getBrowserDeviceLabel()
            }
          });
        }
        return handle(request);
      })
    );
  }
}
